import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { MainLayout } from '../components/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Loader2, Clock, CheckCircle, XCircle, FileSpreadsheet, Mail, RefreshCw, Send } from 'lucide-react';
import { format, subDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BulkDownloads = () => {
  const { user, token } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null); // Tracks which job is being resent
  
  // Export form state
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exportFormat, setExportFormat] = useState('csv');
  const [planId, setPlanId] = useState(null);
  const isFree = planId === 'free';

  const fetchJobs = useCallback(async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/export-jobs`);
      setJobs(data);
    } catch (error) {
      // Jobs endpoint might not exist yet - handle gracefully
      if (error.response?.status !== 404) {
        console.error('Failed to load export jobs');
      }
    } finally {
      setLoading(false);
    }
  }, [currentAccount]);

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      return;
    }
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [currentAccount, fetchJobs]);

  useEffect(() => {
    if (!currentAccount?.id) return;
    api.get('/subscription/current', { params: { account_id: currentAccount.id } })
      .then((r) => setPlanId(r.data?.plan?.id || 'free'))
      .catch(() => {});
  }, [currentAccount?.id]);

  const handleResendEmail = async (jobId) => {
    setResendingEmail(jobId);
    try {
      const { data } = await api.post(`/export-jobs/${jobId}/resend-email`);
      if (data.success) {
        toast.success('Email sent successfully!');
        fetchJobs(); // Refresh to update email_sent status
      } else {
        toast.error(data.error || 'Failed to send email');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send email';
      toast.error(message);
    } finally {
      setResendingEmail(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFree) {
      toast.error('Bulk export is a paid feature — upgrade to enable it.');
      return;
    }

    if (!dateFrom || !dateTo) {
      toast.error('Please select a date range');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/accounts/${currentAccount.id}/export-jobs`, {
        date_from: dateFrom,
        date_to: dateTo,
        format: exportFormat
      });
      toast.success('Export job submitted! You will receive an email when it\'s ready.');
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit export job');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="rounded-none bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'processing':
        return <Badge className="rounded-none bg-yellow-500"><Clock className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive" className="rounded-none"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary" className="rounded-none">{status}</Badge>;
    }
  };

  return (
    <MainLayout>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Form */}
        <Card className="rounded-none border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              New Export
            </CardTitle>
            <CardDescription>
              Export bill data for a date range. You&apos;ll receive an email with the download link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isFree && (
              <div className="mb-4 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="bulk-download-free-gate">
                Bulk export is a paid feature. On the Free plan you can still download bills one at a time from the Bills list.{' '}
                <a href="/billing/choose" className="font-semibold underline">Upgrade to enable bulk downloads.</a>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-none"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-none"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Format</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-2">
                <Button 
                  type="submit" 
                  className="w-full rounded-none"
                  disabled={submitting || isFree}
                  data-testid="bulk-export-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {isFree ? 'Upgrade for bulk export' : (submitting ? 'Submitting...' : 'Start Export')}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Export includes all bill line items with vendor, amounts, and bill file links.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Export Jobs List */}
        <Card className="rounded-none border-border lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold">Export Jobs</CardTitle>
              <CardDescription>Your recent export requests</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="rounded-none"
              onClick={fetchJobs}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No export jobs yet</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first export using the form</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <div 
                    key={job.id} 
                    className="border border-border p-4 flex items-center justify-between"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(job.status)}
                        <Badge variant="outline" className="rounded-none text-xs font-mono">
                          {job.format?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm">
                        <span className="text-muted-foreground">Date range:</span>{' '}
                        <span className="font-mono">{job.date_from}</span> to <span className="font-mono">{job.date_to}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Triggered by <span className="font-medium">{job.triggered_by_name || 'Unknown'}</span> on{' '}
                        {format(new Date(job.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                      {job.status === 'completed' && job.expires_at && (
                        <p className="text-xs text-yellow-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Link expires: {format(new Date(job.expires_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      )}
                      {job.email_sent && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Email sent
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {job.status === 'completed' && job.download_url && (
                        <Button 
                          size="sm" 
                          className="rounded-none"
                          data-testid={`download-btn-${job.id}`}
                          onClick={() => {
                            // Build download URL with token for authentication
                            const downloadUrl = `${API_URL}${job.download_url}?token=${token}`;
                            window.open(downloadUrl, '_blank');
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      )}
                      {job.status === 'completed' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="rounded-none"
                          data-testid={`resend-email-btn-${job.id}`}
                          title={job.email_sent ? "Resend download link email" : "Send download link email"}
                          disabled={resendingEmail === job.id}
                          onClick={() => handleResendEmail(job.id)}
                        >
                          {resendingEmail === job.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="rounded-none border-border mt-6">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Export Delivery</p>
              <p className="text-sm text-muted-foreground">
                When your export is ready, you&apos;ll receive an email at <span className="font-medium">{user?.email}</span> with a download link. 
                Links expire after <span className="font-medium">24 hours</span> for security.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default BulkDownloads;
